import { useEffect, useMemo, useRef, useState } from "react";
import { fetchCatalog, fetchLeaderboard, submitScore } from "./api";
import { audio } from "./game/audio";
import { GameEngine, PLAY_W } from "./game/engine";
import type { GameState } from "./game/engine";
import { Renderer } from "./game/renderer";
import type { Difficulty, Language, Mushroom, LeaderboardEntry, SubmittedScore } from "./types";

type Phase = "loading" | "menu" | "disclaimer" | "playing" | "gameover";

const DISCLAIMER_SKIP_KEY = "mh-disclaimer-skip-v1";

export default function App() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [loadProgress, setLoadProgress] = useState(0);
  const [catalog, setCatalog] = useState<Mushroom[] | null>(null);
  const [isMiddleEast, setIsMiddleEast] = useState(false);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [finalSnapshot, setFinalSnapshot] = useState<ReturnType<GameEngine["snapshot"]> | null>(null);
  const [submitted, setSubmitted] = useState<SubmittedScore | null>(null);
  const [muted, setMuted] = useState(false);
  const [difficulty, setDifficulty] = useState<Difficulty>("beginner");
  const [language, setLanguage] = useState<Language>("english");
  // When the catalog tells us the player is in the Middle East, switch the
  // name picker default to Hebrew (one-shot; user can still override).
  const meBiasAppliedRef = useRef(false);
  useEffect(() => {
    if (isMiddleEast && !meBiasAppliedRef.current) {
      setLanguage("hebrew");
      meBiasAppliedRef.current = true;
    }
  }, [isMiddleEast]);
  const [lastFact, setLastFact] = useState<{ key: number; mushroom: Mushroom } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const prevEngineStateRef = useRef<GameState>("menu");
  const prevCaughtRef = useRef<number>(0);

  // HUD state ticks each frame so React can re-render the overlay numbers
  const [hud, setHud] = useState<{ score: number; streak: number; lives: number; level: number; multiplier: number }>(
    { score: 0, streak: 0, lives: 3, level: 1, multiplier: 1 },
  );

  // ---------- bootstrap ----------

  useEffect(() => {
    (async () => {
      const cat = await fetchCatalog();
      setCatalog(cat.mushrooms);
      setIsMiddleEast(!!cat.is_middle_east);
      const lb = await fetchLeaderboard(10);
      setLeaderboard(lb);
      setPhase("menu");
    })();
  }, []);

  useEffect(() => {
    if (!catalog || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const renderer = new Renderer(canvas);
    rendererRef.current = renderer;
    const engine = new GameEngine(catalog);
    engine.setMiddleEastBias(isMiddleEast);
    engineRef.current = engine;

    // Images load lazily in the renderer as each species first appears, so
    // we don't block the menu on 500 thumbnails. Just mark "loaded" so the
    // menu can show.
    setLoadProgress(100);

    const resize = () => renderer.resize();
    resize();
    window.addEventListener("resize", resize);

    // Animation loop
    const loop = (t: number) => {
      const last = lastTimeRef.current || t;
      const dt = t - last;
      lastTimeRef.current = t;

      if (engineRef.current) engineRef.current.step(dt);
      const snap = engineRef.current?.snapshot();
      if (snap && renderer) renderer.render(snap, t);

      if (snap) {
        const prevState = prevEngineStateRef.current;
        prevEngineStateRef.current = snap.state;

        if (snap.state === "playing") {
          // Sync HUD (cheap shallow comparisons)
          setHud((prev) => {
            if (
              prev.score === snap.score && prev.streak === snap.streak &&
              prev.lives === snap.lives && prev.level === snap.level &&
              prev.multiplier === snap.multiplier
            ) return prev;
            return { score: snap.score, streak: snap.streak, lives: snap.lives, level: snap.level, multiplier: snap.multiplier };
          });
          // Capture fact only when the caught counter actually advances —
          // snap.lastCaught persists across frames, so polling it would
          // mis-attribute stale info to whatever the player just caught.
          if (snap.caught !== prevCaughtRef.current) {
            const isNewCatch = snap.caught > prevCaughtRef.current;
            prevCaughtRef.current = snap.caught;
            if (
              isNewCatch && snap.lastCaught && snap.lastCaught.fun_fact &&
              (snap.lastCaughtCategory?.startsWith("edible")
                || snap.lastCaughtCategory === "medicinal"
                || snap.lastCaughtCategory === "inedible")
            ) {
              setLastFact({ key: performance.now(), mushroom: snap.lastCaught });
            }
          }
        } else if (snap.state === "gameover" && prevState === "playing") {
          setFinalSnapshot(snap);
          setPhase("gameover");
          audio.stopMusic();
          // Fetch updated leaderboard for tier comparison
          fetchLeaderboard(10).then(setLeaderboard);
        }
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, isMiddleEast]);

  // ---------- input ----------

  useEffect(() => {
    const onKey = (e: KeyboardEvent, down: boolean) => {
      if (!engineRef.current) return;
      // Don't swallow typing in inputs (e.g. the name-entry field on game over).
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      const e2 = e as KeyboardEvent;
      if (e2.code === "ArrowLeft" || e2.code === "KeyA") {
        engineRef.current.setLeft(down);
        e.preventDefault();
      } else if (e2.code === "ArrowRight" || e2.code === "KeyD") {
        engineRef.current.setRight(down);
        e.preventDefault();
      } else if (down && e2.code === "Space" && phase === "menu") {
        requestStart();
      } else if (down && e2.code === "KeyM") {
        toggleMute();
      }
    };
    const onDown = (e: KeyboardEvent) => onKey(e, true);
    const onUp = (e: KeyboardEvent) => onKey(e, false);
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!engineRef.current || !rendererRef.current) return;
    if (phase !== "playing") return;
    const wx = rendererRef.current.clientToWorldX(e.clientX);
    engineRef.current.setPointer(Math.max(0, Math.min(PLAY_W, wx)));
  };

  const onPointerLeave = () => {
    if (engineRef.current) engineRef.current.setPointer(null);
  };

  // ---------- actions ----------

  function requestStart() {
    try {
      if (localStorage.getItem(DISCLAIMER_SKIP_KEY) === "1") {
        beginGame();
        return;
      }
    } catch {
      // localStorage may be unavailable (private mode, etc.) — fall through to show.
    }
    setPhase("disclaimer");
  }

  function acceptDisclaimer(dontShowAgain: boolean) {
    if (dontShowAgain) {
      try { localStorage.setItem(DISCLAIMER_SKIP_KEY, "1"); } catch { /* ignore */ }
    }
    beginGame();
  }

  function beginGame() {
    audio.init();
    audio.setMuted(muted);
    audio.startMusic();
    audio.play("menu_select");
    rendererRef.current?.setDifficulty(difficulty);
    rendererRef.current?.setLanguage(language);
    engineRef.current?.setRequiredLanguage(
      language === "english" ? "english" : language === "russian" ? "russian" : null,
    );
    engineRef.current?.start();
    setPhase("playing");
    setSubmitted(null);
    setLastFact(null);
    setFinalSnapshot(null);
    prevCaughtRef.current = 0;
  }

  // Auto-start when ?autostart is in the URL (handy for demos / sharing direct links).
  useEffect(() => {
    if (phase !== "menu" || !catalog) return;
    if (new URLSearchParams(window.location.search).has("autostart")) {
      requestStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, catalog]);

  function backToMenu() {
    audio.stopMusic();
    engineRef.current?.goToMenu();
    prevEngineStateRef.current = "menu";
    prevCaughtRef.current = 0;
    setPhase("menu");
    setFinalSnapshot(null);
    setSubmitted(null);
    setLastFact(null);
  }

  function toggleMute() {
    setMuted((m) => {
      const next = !m;
      audio.setMuted(next);
      return next;
    });
  }

  async function submit(name: string) {
    if (!finalSnapshot) return;
    const result = await submitScore({
      name,
      score: finalSnapshot.score,
      streak: finalSnapshot.bestStreak,
      caught: finalSnapshot.caught,
      duration_ms: Math.round(finalSnapshot.durationMs),
    });
    setSubmitted(result);
    const lb = await fetchLeaderboard(10);
    setLeaderboard(lb);
  }

  // ---------- render ----------

  const isLoading = phase === "loading" || (!catalog) || (loadProgress < 100 && phase === "menu");

  return (
    <div className="app">
      <div className="cabinet">
        <button
          className={`audio-toggle ${phase === "playing" ? "audio-toggle-bottom" : ""}`}
          onClick={toggleMute}
          aria-label="Toggle audio"
        >
          {muted ? "🔇" : "🔊"}
        </button>

        <canvas
          ref={canvasRef}
          className="game-canvas"
          onPointerMove={onPointerMove}
          onPointerDown={onPointerMove}
          onPointerLeave={onPointerLeave}
        />

        {phase === "playing" && <HudOverlay hud={hud} />}
        {phase === "playing" && lastFact && (
          <div className="fact-toast" key={lastFact.key}>
            <strong>
              {(language === "latin" ? lastFact.mushroom.scientific_name : lastFact.mushroom.common_name).toUpperCase()}
            </strong>
            <br />
            {lastFact.mushroom.fun_fact}
          </div>
        )}

        {phase === "menu" && !isLoading && (
          <Menu
            onStart={requestStart}
            leaderboard={leaderboard}
            difficulty={difficulty}
            onDifficultyChange={setDifficulty}
            language={language}
            onLanguageChange={setLanguage}
            isMiddleEast={isMiddleEast}
          />
        )}

        {phase === "disclaimer" && (
          <Disclaimer onContinue={acceptDisclaimer} onCancel={backToMenu} />
        )}

        {phase === "gameover" && finalSnapshot && (
          <GameOverScreen
            snap={finalSnapshot}
            leaderboard={leaderboard}
            submitted={submitted}
            language={language}
            onSubmit={submit}
            onPlayAgain={requestStart}
            onMenu={backToMenu}
          />
        )}

        {isLoading && (
          <div className="overlay">
            <div className="loading">
              <div>LOADING FOREST…</div>
              <div className="loading-bar"><div style={{ width: `${loadProgress}%` }} /></div>
              <div style={{ fontSize: 9, opacity: 0.7 }}>{loadProgress}%</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- HUD ----------

function HudOverlay({ hud }: { hud: { score: number; streak: number; lives: number; level: number; multiplier: number } }) {
  return (
    <div className="hud">
      <div className="col">
        <div>
          <div className="label">SCORE</div>
          <div className="value score">{hud.score.toString().padStart(6, "0")}</div>
        </div>
        <div>
          <div className="label">LEVEL</div>
          <div className="value">{hud.level}</div>
        </div>
      </div>
      <div className="col right">
        <div className="lives" aria-label={`${hud.lives} lives`}>
          {[0, 1, 2].map((i) => (
            <span key={i} className={`heart ${i >= hud.lives ? "spent" : ""}`} />
          ))}
        </div>
        <div>
          <div className="label" style={{ textAlign: "right" }}>STREAK</div>
          <div className="value streak" style={{ textAlign: "right" }}>x{hud.streak}</div>
        </div>
        {hud.multiplier > 1 && <div className="mult-badge">x{hud.multiplier}</div>}
      </div>
    </div>
  );
}

// ---------- Menu ----------

function Menu({
  onStart,
  leaderboard,
  difficulty,
  onDifficultyChange,
  language,
  onLanguageChange,
  isMiddleEast,
}: {
  onStart: () => void;
  leaderboard: LeaderboardEntry[];
  difficulty: Difficulty;
  onDifficultyChange: (d: Difficulty) => void;
  language: Language;
  onLanguageChange: (l: Language) => void;
  isMiddleEast: boolean;
}) {
  const podium = leaderboard.slice(0, 3);
  return (
    <div className="overlay menu-overlay">
      <HeroScene />
      <h1 className="title">MUSHROOM<br />HUNT</h1>

      <div className="picker-row">
        <div className="difficulty-picker">
          <div className="difficulty-label">DIFFICULTY</div>
          <div className="difficulty-options">
            <button
              className={`diff-btn ${difficulty === "beginner" ? "active" : ""}`}
              onClick={() => onDifficultyChange("beginner")}
            >
              BEGINNER
            </button>
            <button
              className={`diff-btn ${difficulty === "advanced" ? "active" : ""}`}
              onClick={() => onDifficultyChange("advanced")}
            >
              ADVANCED
            </button>
          </div>
        </div>

        <div className="difficulty-picker">
          <div className="difficulty-label">LANGUAGE</div>
          <div className="difficulty-options language-options">
            {isMiddleEast && (
              <button
                className={`diff-btn diff-btn-hebrew ${language === "hebrew" ? "active" : ""}`}
                onClick={() => onLanguageChange("hebrew")}
              >
                עברית
              </button>
            )}
            <button
              className={`diff-btn ${language === "english" ? "active" : ""}`}
              onClick={() => onLanguageChange("english")}
            >
              ENGLISH
            </button>
            <button
              className={`diff-btn ${language === "russian" ? "active" : ""}`}
              onClick={() => onLanguageChange("russian")}
            >
              РУССКИЙ
            </button>
            <button
              className={`diff-btn ${language === "latin" ? "active" : ""}`}
              onClick={() => onLanguageChange("latin")}
            >
              LATIN
            </button>
          </div>
        </div>
      </div>

      <div className="btn-row">
        <button className="btn" onClick={onStart}>▶ START</button>
      </div>

      {podium.length > 0 && (
        <div className="lb-panel menu-lb">
          <div className="lb-title">TOP FORAGERS</div>
          <div className="lb-row header">
            <span className="lb-rank">#</span>
            <span className="lb-name">NAME</span>
            <span className="lb-score">SCORE</span>
            <span className="lb-streak">STRK</span>
          </div>
          {podium.map((row) => (
            <div className="lb-row" key={`${row.rank}-${row.name}-${row.created_at}`}>
              <span className="lb-rank">{row.rank}</span>
              <span className="lb-name">{row.name}</span>
              <span className="lb-score">{row.score}</span>
              <span className="lb-streak">x{row.streak}</span>
            </div>
          ))}
        </div>
      )}

      <div className="social-row">
        <a
          className="fb-link"
          href="https://www.facebook.com/groups/israelmushrooms"
          target="_blank"
          rel="noopener noreferrer"
          title="Israel Mushrooms — Facebook group"
          aria-label="Israel Mushrooms — Facebook group"
        >
          <svg
            className="fb-icon"
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <rect width="32" height="32" rx="7" fill="#1877F2" />
            <path
              d="M21 17.5h-3.3V28h-4.1V17.5H11v-3.7h2.6v-2.5c0-2.9 1.7-4.8 5-4.8 1 0 1.7.1 2.5.2v3.4h-1.9c-1.4 0-1.9.6-1.9 2v1.7h3.8L21 17.5z"
              fill="#fff"
            />
          </svg>
        </a>

        <a
          className="fb-link"
          href="https://wa.me/972549980981"
          target="_blank"
          rel="noopener noreferrer"
          title="Chat on WhatsApp"
          aria-label="Chat on WhatsApp"
        >
          <svg
            className="fb-icon"
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <rect width="32" height="32" rx="7" fill="#25D366" />
            <path
              d="M22.8 18.6c-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.2-.2.3-.9 1.1-1.1 1.4-.2.2-.4.3-.7.1-.3-.2-1.4-.5-2.7-1.7-1-.9-1.7-2-1.9-2.3-.2-.3 0-.5.1-.7.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.1 1.1-1.1 2.6 0 1.5 1.1 3 1.3 3.2.2.2 2.2 3.3 5.3 4.7.7.3 1.3.5 1.8.6.7.2 1.4.2 2 .1.6-.1 1.9-.8 2.1-1.5.3-.7.3-1.4.2-1.5-.1-.1-.3-.2-.6-.4z"
              fill="#fff"
            />
          </svg>
        </a>

        <a
          className="fb-link"
          href="https://t.me/israel_fungi"
          target="_blank"
          rel="noopener noreferrer"
          title="Israel Fungi — Telegram"
          aria-label="Israel Fungi — Telegram"
        >
          <svg
            className="fb-icon"
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <rect width="32" height="32" rx="7" fill="#229ED9" />
            <path
              d="M24.4 8.7l-3.1 14.8c-.2 1-.8 1.3-1.7.8l-4.7-3.4-2.3 2.2c-.3.3-.5.5-.9.5l.3-4.7L21 11c.3-.3-.1-.5-.5-.2L9.4 17.7l-4.5-1.4c-1-.3-1-1 .2-1.5L23 8c.8-.3 1.5.2 1.4 1.5z"
              fill="#fff"
            />
          </svg>
        </a>
      </div>

    </div>
  );
}

// ---------- Disclaimer ----------

function Disclaimer({ onContinue, onCancel }: { onContinue: (dontShowAgain: boolean) => void; onCancel: () => void }) {
  const [dontShow, setDontShow] = useState(false);
  return (
    <div className="overlay disclaimer-overlay">
      <div className="disclaimer-stage">
        <div className="speech-bubble">
          <div className="bubble-line">
            <strong>HEY THERE, FORAGER!</strong>
          </div>
          <div className="bubble-line">
            This game is just for fun — it does <em>not</em> encourage anyone to eat wild mushrooms.
          </div>
          <div className="bubble-line">
            In the real forest, <strong>always</strong> forage with an expert who can identify mushrooms safely.
          </div>
        </div>
        <Gnome />
      </div>

      <label className="dont-show-again">
        <input
          type="checkbox"
          checked={dontShow}
          onChange={(e) => setDontShow(e.target.checked)}
        />
        <span>DON'T SHOW THIS AGAIN</span>
      </label>

      <div className="btn-row">
        <button className="btn" onClick={() => onContinue(dontShow)}>I UNDERSTAND</button>
        <button className="btn secondary" onClick={onCancel}>BACK</button>
      </div>
    </div>
  );
}

function HeroScene() {
  // Pixel-art forest-floor scene with five mushrooms. Scales with the SVG.
  return (
    <svg
      className="hero-scene"
      viewBox="0 0 96 24"
      preserveAspectRatio="xMidYMid meet"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {/* sky bands — match the renderer's morning-forest palette */}
      <rect x="0" y="0" width="96" height="2" fill="#7e9aa4" />
      <rect x="0" y="2" width="96" height="2" fill="#9aa898" />
      <rect x="0" y="4" width="96" height="2" fill="#b8a888" />
      <rect x="0" y="6" width="96" height="2" fill="#a07a4a" />
      <rect x="0" y="8" width="96" height="2" fill="#7a5a3a" />
      <rect x="0" y="10" width="96" height="5" fill="#5a3a1e" />

      {/* sun */}
      <rect x="76" y="2" width="4" height="3" fill="#f8e8b8" />
      <rect x="77" y="1" width="2" height="1" fill="#f8e8b8" />
      <rect x="77" y="5" width="2" height="1" fill="#f8e8b8" />

      {/* distant mountains */}
      <polygon points="0,10 10,5 21,10" fill="#7a6850" />
      <polygon points="18,10 30,4 42,10" fill="#7a6850" />
      <polygon points="40,10 52,6 64,10" fill="#7a6850" />
      <polygon points="62,10 74,3 86,10" fill="#7a6850" />
      <polygon points="84,10 92,6 96,10" fill="#7a6850" />

      {/* trees back (forest green silhouettes) */}
      <polygon points="2,15 6,7 10,15" fill="#1f3a22" />
      <polygon points="11,15 16,5 21,15" fill="#1f3a22" />
      <polygon points="25,15 29,8 33,15" fill="#1f3a22" />
      <polygon points="42,15 47,4 52,15" fill="#1f3a22" />
      <polygon points="60,15 64,8 68,15" fill="#1f3a22" />
      <polygon points="78,15 84,6 90,15" fill="#1f3a22" />

      {/* trees front (darker) */}
      <polygon points="0,15 4,9 8,15" fill="#10210f" />
      <polygon points="34,15 38,10 42,15" fill="#10210f" />
      <polygon points="50,15 55,8 60,15" fill="#10210f" />
      <polygon points="68,15 72,10 76,15" fill="#10210f" />
      <polygon points="90,15 94,9 96,15" fill="#10210f" />

      {/* ground */}
      <rect x="0" y="15" width="96" height="9" fill="#3a2010" />
      <rect x="0" y="15" width="96" height="1" fill="#5a3018" />
      {/* grass tufts */}
      <rect x="3" y="15" width="1" height="1" fill="#5a8a28" />
      <rect x="11" y="15" width="1" height="1" fill="#5a8a28" />
      <rect x="29" y="15" width="1" height="1" fill="#5a8a28" />
      <rect x="44" y="15" width="1" height="1" fill="#5a8a28" />
      <rect x="62" y="15" width="1" height="1" fill="#5a8a28" />
      <rect x="80" y="15" width="1" height="1" fill="#5a8a28" />

      {/* Mushroom 1 — Big Amanita muscaria (red w/ white spots) at cx=20 */}
      <rect x="19" y="5" width="3" height="1" fill="#c4423a" />
      <rect x="17" y="6" width="7" height="1" fill="#c4423a" />
      <rect x="15" y="7" width="11" height="1" fill="#c4423a" />
      <rect x="15" y="8" width="11" height="1" fill="#9a2e26" />
      <rect x="17" y="7" width="1" height="1" fill="#f8f0d0" />
      <rect x="20" y="7" width="1" height="1" fill="#f8f0d0" />
      <rect x="23" y="7" width="1" height="1" fill="#f8f0d0" />
      <rect x="17" y="9" width="7" height="1" fill="#3a2418" />
      <rect x="19" y="10" width="3" height="4" fill="#e8d8b0" />
      <rect x="18" y="14" width="5" height="1" fill="#e8d8b0" />

      {/* Mushroom 2 — Chanterelle (golden trumpet) at cx=38 */}
      <rect x="37" y="8" width="3" height="1" fill="#e8a93a" />
      <rect x="35" y="9" width="7" height="1" fill="#e8a93a" />
      <rect x="35" y="10" width="7" height="1" fill="#a06820" />
      <rect x="37" y="11" width="3" height="3" fill="#e8d8b0" />
      <rect x="36" y="14" width="5" height="1" fill="#e8d8b0" />

      {/* Mushroom 3 — Porcini (brown, thick stem) at cx=56 */}
      <rect x="54" y="7" width="5" height="1" fill="#a06030" />
      <rect x="52" y="8" width="9" height="1" fill="#a06030" />
      <rect x="52" y="9" width="9" height="1" fill="#5a3018" />
      <rect x="54" y="10" width="5" height="4" fill="#e8d8b0" />
      <rect x="53" y="14" width="7" height="1" fill="#e8d8b0" />

      {/* Mushroom 4 — Amethyst (purple) at cx=74 */}
      <rect x="73" y="9" width="3" height="1" fill="#9a7ad0" />
      <rect x="71" y="10" width="7" height="1" fill="#7a5ab0" />
      <rect x="71" y="11" width="7" height="1" fill="#4a3a80" />
      <rect x="73" y="12" width="3" height="2" fill="#e8d8b0" />
      <rect x="72" y="14" width="5" height="1" fill="#e8d8b0" />

      {/* Mushroom 5 — Baby Amanita (small) at cx=88 */}
      <rect x="86" y="12" width="5" height="1" fill="#c4423a" />
      <rect x="87" y="11" width="3" height="1" fill="#c4423a" />
      <rect x="86" y="13" width="5" height="1" fill="#9a2e26" />
      <rect x="87" y="14" width="3" height="1" fill="#e8d8b0" />
    </svg>
  );
}

function Gnome() {
  // Pixel-art gnome rendered as inline SVG with crisp edges. Scales with the SVG.
  return (
    <svg
      className="gnome"
      viewBox="0 0 16 24"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      {/* hat */}
      <rect x="6" y="0" width="4" height="1" fill="#c4423a" />
      <rect x="5" y="1" width="6" height="1" fill="#c4423a" />
      <rect x="4" y="2" width="8" height="1" fill="#c4423a" />
      <rect x="4" y="3" width="8" height="1" fill="#a02e28" />
      <rect x="3" y="4" width="10" height="1" fill="#c4423a" />
      <rect x="3" y="5" width="10" height="1" fill="#a02e28" />
      <rect x="2" y="6" width="12" height="1" fill="#c4423a" />
      {/* hat brim shadow */}
      <rect x="2" y="7" width="12" height="1" fill="#6b1a16" />
      {/* face */}
      <rect x="4" y="8" width="8" height="1" fill="#f0c89a" />
      <rect x="3" y="9" width="10" height="1" fill="#f0c89a" />
      {/* eyes */}
      <rect x="5" y="9" width="1" height="1" fill="#0c0606" />
      <rect x="10" y="9" width="1" height="1" fill="#0c0606" />
      {/* nose */}
      <rect x="7" y="10" width="2" height="1" fill="#d49a6c" />
      {/* beard */}
      <rect x="3" y="10" width="3" height="1" fill="#f3e3c5" />
      <rect x="10" y="10" width="3" height="1" fill="#f3e3c5" />
      <rect x="2" y="11" width="12" height="1" fill="#f3e3c5" />
      <rect x="2" y="12" width="12" height="1" fill="#f3e3c5" />
      <rect x="3" y="13" width="10" height="1" fill="#f3e3c5" />
      {/* mouth */}
      <rect x="7" y="12" width="2" height="1" fill="#9a3a2a" />
      {/* body / tunic */}
      <rect x="4" y="14" width="8" height="1" fill="#4a6b2a" />
      <rect x="3" y="15" width="10" height="1" fill="#4a6b2a" />
      <rect x="3" y="16" width="10" height="1" fill="#3a5a20" />
      <rect x="2" y="17" width="12" height="1" fill="#4a6b2a" />
      <rect x="2" y="18" width="12" height="1" fill="#3a5a20" />
      {/* belt */}
      <rect x="2" y="19" width="12" height="1" fill="#2a1a10" />
      <rect x="7" y="19" width="2" height="1" fill="#ffd84d" />
      {/* boots */}
      <rect x="3" y="20" width="3" height="2" fill="#2a1a10" />
      <rect x="10" y="20" width="3" height="2" fill="#2a1a10" />
      <rect x="2" y="22" width="5" height="1" fill="#1a0d08" />
      <rect x="9" y="22" width="5" height="1" fill="#1a0d08" />
    </svg>
  );
}

// ---------- Game Over ----------

function GameOverScreen({
  snap,
  leaderboard,
  submitted,
  language,
  onSubmit,
  onPlayAgain,
  onMenu,
}: {
  snap: ReturnType<GameEngine["snapshot"]>;
  leaderboard: LeaderboardEntry[];
  submitted: SubmittedScore | null;
  language: Language;
  onSubmit: (name: string) => void;
  onPlayAgain: () => void;
  onMenu: () => void;
}) {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => inputRef.current?.focus(), []);

  const heading = useMemo(() => {
    if (snap.causeOfDeath) return "YOU DIED";
    return "GAME OVER";
  }, [snap.causeOfDeath]);

  const deathName = snap.causeOfDeath
    ? (language === "latin" ? snap.causeOfDeath.scientific_name : snap.causeOfDeath.common_name)
    : "";

  return (
    <div className="overlay">
      <h1 className="title" style={{ fontSize: 36 }}>{heading}</h1>
      {snap.causeOfDeath && (
        <div className="cause">
          {snap.causeOfDeath.category === "deadly" ? "KILLED BY" : "OUT OF HEARTS — LAST HIT"}
          <img src={snap.causeOfDeath.image_url} alt={deathName} />
          <span className={/[֐-׿]/.test(deathName) ? "cause-name cause-name-hebrew" : "cause-name"}>
            {deathName.toUpperCase()}
          </span>
        </div>
      )}
      <div style={{ fontSize: 10, opacity: 0.7, letterSpacing: 1 }}>FINAL SCORE</div>
      <div className="score-final">{snap.score.toString().padStart(6, "0")}</div>
      <div style={{ fontSize: 10, opacity: 0.7 }}>
        CAUGHT {snap.caught} · BEST STREAK x{snap.bestStreak}
      </div>

      {!submitted ? (
        <>
          <input
            ref={inputRef}
            className="name-input"
            value={name}
            onChange={(e) => setName(e.target.value.toUpperCase().slice(0, 12))}
            placeholder="AAA"
            maxLength={12}
            onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onSubmit(name.trim()); }}
          />
          <div className="btn-row">
            <button className="btn" disabled={!name.trim()} onClick={() => onSubmit(name.trim())}>
              SUBMIT
            </button>
            <button className="btn secondary" onClick={onPlayAgain}>
              RETRY
            </button>
            <button className="btn secondary" onClick={onMenu}>
              MENU
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: 12, color: "var(--gold)", margin: "10px 0 18px" }}>
            {submitted.rank ? `YOU RANK #${submitted.rank} WORLDWIDE` : "SCORE SAVED"}
          </div>
          <div className="lb-panel" style={{ maxHeight: 220, overflow: "auto" }}>
            <div className="lb-title">LEADERBOARD</div>
            <div className="lb-row header">
              <span className="lb-rank">#</span>
              <span className="lb-name">NAME</span>
              <span className="lb-score">SCORE</span>
              <span className="lb-streak">STRK</span>
            </div>
            {leaderboard.map((row) => (
              <div
                className={`lb-row ${row.score === submitted.score && row.name === submitted.name ? "me" : ""}`}
                key={`${row.rank}-${row.name}-${row.created_at}`}
              >
                <span className="lb-rank">{row.rank}</span>
                <span className="lb-name">{row.name}</span>
                <span className="lb-score">{row.score}</span>
                <span className="lb-streak">x{row.streak}</span>
              </div>
            ))}
          </div>
          <div className="btn-row" style={{ marginTop: 18 }}>
            <button className="btn" onClick={onPlayAgain}>PLAY AGAIN</button>
            <button className="btn secondary" onClick={onMenu}>MENU</button>
          </div>
          <div className="share-line">
            SHARE YOUR SCORE — TAG #MUSHROOMHUNT
          </div>
        </>
      )}
    </div>
  );
}
